/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2 as Grid,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { fetchInstitutions, fetchRoles } from "../../app/slices/commonSlice";
import { validate } from "validate.js";
import {
  registerUser,
  resetStateUsers,
  updateUser,
} from "../../app/slices/userSlice";
import AppMessage from "../../components/AppMessage/AppMessage";

const UserDialog = ({ open, onClose, title, selectedUser, method = "ADD" }) => {
  const { data: institutions, status: statusInstitutions } = useSelector(
    ({ common }) => common.institutions
  );
  const { data: roles, status: statusRoles } = useSelector(
    ({ common }) => common.roles
  );
  const {
    data: dataPost,
    status: statusPost,
    error: errorPost,
  } = useSelector(({ users }) => users.post);
  const {
    data: dataPut,
    status: statusPut,
    error: errorPut,
  } = useSelector(({ users }) => users.put);

  const statuses = [
    { value: "A", label: "Active" },
    { value: "I", label: "Inactive" },
    { value: "D", label: "Disabled" },
  ];

  const schema = {
    fullName: {
      presence: { allowEmpty: false },
    },
    email: {
      presence: { allowEmpty: false },
      email: true,
    },
    phone: {
      presence: { allowEmpty: false },
    },
    role: {
      presence: { allowEmpty: false },
    },
    institution: {
      presence: { allowEmpty: false },
    },
  };

  const [formValues, setFormValues] = useState(selectedUser || {});
  const [errors, setErrors] = useState([]);
  const [response, setResponse] = useState(null);
  const dispatch = useDispatch();

  //use effect
  useEffect(() => {
    dispatch(fetchInstitutions({ status: "A" }));
    dispatch(fetchRoles());
    dispatch(resetStateUsers(["post"]));
    dispatch(resetStateUsers(["put"]));
    return () => {
      dispatch(resetStateUsers(["post"]));
      dispatch(resetStateUsers(["put"]));
    };
  }, []);

  useEffect(() => {
    if (errorPost || errorPut) {
      setResponse({
        type: "error",
        message: errorPost?.message || errorPut?.message,
      });
    }
  }, [errorPost, errorPut]);

  useEffect(() => {
    if (dataPost || dataPut) {
      setResponse({
        type: "success",
        message: method === "ADD" ? "User registered successfully" : dataPut,
      });
    }
  }, [dataPost, dataPut]);

  //methods
  const handleSubmit = () => {
    let _errors = validate(
      formValues,
      method === "ADD"
        ? schema
        : {
            ...schema,
            status: {
              presence: { allowEmpty: false },
            },
          }
    );
    if (_errors) {
      setErrors(_errors);
      return;
    } else {
      setErrors([]);
    }

    if (method === "ADD") {
      dispatch(resetStateUsers(["post"]));
      dispatch(
        registerUser({
          full_name: formValues?.fullName,
          phone: formValues?.phone,
          email: formValues?.email?.toLowerCase(),
          role_id: formValues?.role,
          institution_id: formValues?.institution,
        })
      );
    } else {
      dispatch(resetStateUsers(["put"]));
      dispatch(
        updateUser({
          id: selectedUser?.user_id,
          payload: {
            full_name: formValues?.fullName,
            phone: formValues?.phone,
            role_id: formValues?.role,
            institution_id: formValues?.institution,
            status: formValues?.status,
          },
        })
      );
    }
  };

  const handleChange = (e) => {
    const inputName = e?.target?.name;
    const inputValue = e?.target?.value;

    if (inputName === "phone") {
      const re = /^[0-9,]*$/;
      if (re.test(inputValue)) {
        setFormValues({
          ...formValues,
          phone: inputValue,
        });
      }
    } else {
      setFormValues({
        ...formValues,
        [inputName]: inputValue,
      });
    }
  };

  //builders
  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
    >
      <DialogTitle id="responsive-dialog-title">
        <span id="modal-header">
          <b>{title?.toUpperCase()}</b>
        </span>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <div className="p-10">
            <Grid container spacing={2}>
              <Grid size={{ md: 6, xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  name="fullName"
                  id="full_name"
                  type="text"
                  label="Full Name"
                  error={errors["fullName"] ? true : false}
                  helperText={errors["fullName"] ? errors.fullName[0] : null}
                  onChange={handleChange}
                  value={formValues?.fullName}
                />
              </Grid>
              <Grid size={{ md: 6, xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  name="email"
                  id="email"
                  type="email"
                  label="Email"
                  error={errors["email"] ? true : false}
                  helperText={errors["email"] ? errors.email[0] : null}
                  onChange={handleChange}
                  value={formValues?.email}
                />
              </Grid>
              <Grid size={{ md: 6, xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  name="phone"
                  id="phone"
                  type="phone"
                  label="Phone"
                  error={errors["phone"] ? true : false}
                  helperText={errors["phone"] ? errors.phone[0] : null}
                  onChange={handleChange}
                  value={formValues?.phone}
                />
              </Grid>
              <Grid size={{ md: 6, xs: 12 }}>
                {roles && statusRoles === "idle" && (
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    name="role"
                    id="role"
                    select
                    label="Role"
                    error={errors["role"] ? true : false}
                    helperText={errors["role"] ? errors.role[0] : null}
                    onChange={handleChange}
                    value={formValues?.role}
                  >
                    {roles.map((option) => {
                      return (
                        <MenuItem value={option.role_id}>
                          {option.role_name}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}
              </Grid>
              <Grid size={{ md: 6, xs: 12 }}>
                {institutions && statusInstitutions === "idle" && (
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    name="institution"
                    id="institution"
                    select
                    label="Institution"
                    error={errors["institution"] ? true : false}
                    helperText={
                      errors["institution"] ? errors.institution[0] : null
                    }
                    onChange={handleChange}
                    value={formValues?.institution}
                  >
                    {institutions.map((option) => {
                      return (
                        <MenuItem value={option.institution_id}>
                          {option.institution_name}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}
              </Grid>
              {method === "UPDATE" && (
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    name="status"
                    id="incident-rating"
                    select
                    label="Status"
                    error={errors["status"] ? true : false}
                    helperText={errors["status"] ? errors.status[0] : null}
                    onChange={handleChange}
                    value={formValues?.status}
                  >
                    {statuses.map((option) => {
                      return (
                        <MenuItem value={option.value}>{option.label}</MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
              )}
            </Grid>
          </div>
          {response && (
            <AppMessage
              isOpen={response !== null}
              message={response?.message}
              messageType={response?.type}
              onClose={() => {
                setResponse(null);
              }}
            />
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {dataPost === null && dataPut === null && (
          <Button
            id="btn-confirm"
            variant="contained"
            size="small"
            disabled={statusPost === "loading" || statusPut === "loading"}
            onClick={() => {
              handleSubmit();
            }}
            color="primary"
          >
            {(statusPost === "loading" || statusPut === "loading") && (
              <CircularProgress
                size={20}
                color="white"
                style={{ marginRight: "5px" }}
              />
            )}
            {method === "ADD" ? "Register" : "Update"}
          </Button>
        )}

        <Button
          id="btn-close"
          variant="contained"
          size="small"
          onClick={() => {
            onClose(dataPost || dataPut ? true : false);
          }}
          color="error"
        >
          <CloseIcon fontSize="small" className="app-button-icon" /> Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDialog;
